<script setup lang="ts">
    import { ref, computed, onMounted } from 'vue';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import { getLibraryLink, getClientApplicationDetailsLink } from '@/shared/helpers/routing';
    import {
        getLibrarySubscriptionsAndWebhooks,
        getVirtualLibraryDependencies
    } from '@/shared/helpers/api.ts';
    import type { Library, LibrarySubscriptionsAndWebhooks } from '@/shared/helpers/api/types/entities.ts';
    import {
        loadLibraryClientDependencies,
        groupClientDependencies,
        type ClientDependencyRow
    } from './LibraryRelations.helper.ts';
    import { http } from '@/shared/httpWrapper';
    import { truncate, shortDate } from '@/formatters.ts';

    const props = defineProps<{
        library: Library;
    }>();

    const loading = ref<boolean>(true);
    const virtualLibraryDependencies = ref<string[]>([]);
    const hooksAndSubs = ref<LibrarySubscriptionsAndWebhooks | null>(null);
    const clientDependencies = ref<ClientDependencyRow[]>([]);

    const webhooks = computed(() => hooksAndSubs.value?.webhooks ?? []);
    const subscriptions = computed(() => hooksAndSubs.value?.subscriptions ?? []);

    const groupedClientDependencies = computed(() => groupClientDependencies(clientDependencies.value));

    async function loadRelationsData() {
        loading.value = true;
        try {
            const [subscriptionsAndWebhooks, dependencies, virtualDependencies] = await Promise.all([
                getLibrarySubscriptionsAndWebhooks(http, props.library.name),
                loadLibraryClientDependencies(props.library.name),
                props.library.isVirtual
                    ? getVirtualLibraryDependencies(http, props.library.name)
                    : Promise.resolve<string[]>([])
            ]);
            hooksAndSubs.value = subscriptionsAndWebhooks;
            clientDependencies.value = dependencies;
            virtualLibraryDependencies.value = virtualDependencies;
        } finally {
            loading.value = false;
        }
    }

    onMounted(loadRelationsData);
</script>

<template>
    <div>
        <b-loading
            :is-full-page="false"
            :model-value="loading" />
        <div
            v-if="virtualLibraryDependencies.length > 0"
            class="block">
            <p class="title is-5">
                Virtual library dependencies
            </p>
            <p class="block">
                This virtual library depends on data from the following libraries.
                Any changes to these libraries may trigger changes in <i>{{ library.name }}</i> as well.
            </p>
            <ul class="bullet-list">
                <li
                    v-for="dependency in virtualLibraryDependencies"
                    :key="dependency">
                    <router-link :to="getLibraryLink(dependency)">
                        {{ dependency }}
                    </router-link>
                </li>
            </ul>
        </div>
        <div class="block">
            <p class="title is-5">
                Subscriptions
            </p>
            <b-table
                v-if="subscriptions.length > 0"
                :data="subscriptions"
                :narrowed="true"
                :striped="true"
                :hoverable="true">
                <b-table-column
                    v-slot="tableProps"
                    field="application.name"
                    label="Application"
                    sortable>
                    {{ tableProps.row.application.name }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="enabled"
                    label="Status"
                    sortable>
                    <b-icon
                        :icon="tableProps.row.enabled ? 'check-circle' : 'close-circle'"
                        size="is-small"
                        :style="{ color: tableProps.row.enabled ? 'green' : 'red' }" />
                    {{ tableProps.row.enabled ? 'Enabled' : 'Disabled' }}
                </b-table-column>
            </b-table>
            <p
                v-else
                class="has-text-grey">
                This library has no subscriptions that push changes to external systems.
            </p>
        </div>
        <div
            v-if="webhooks.length > 0"
            class="block">
            <p class="title is-5">
                Webhooks
            </p>
            <b-table
                :data="webhooks"
                :narrowed="true"
                :striped="true"
                :hoverable="true">
                <b-table-column
                    v-slot="tableProps"
                    field="callbackUrl"
                    label="Callback Url"
                    sortable>
                    <b-tooltip
                        :active="tableProps.row.callbackUrl.length > 30"
                        :label="tableProps.row.callbackUrl"
                        position="is-bottom">
                        {{ truncate(tableProps.row.callbackUrl, 30) }}
                    </b-tooltip>
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="connectionStatus"
                    label="Status"
                    sortable>
                    {{ tableProps.row.connectionStatus }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="enabled"
                    label="Enabled"
                    sortable>
                    <bool-element :value="tableProps.row.enabled" />
                </b-table-column>
            </b-table>
        </div>
        <div
            v-if="groupedClientDependencies.length > 0"
            class="block">
            <p class="title is-5">
                Client dependencies
            </p>
            <p class="block">
                The following client applications have been found to consume data from this library.
                <em>Observed attributes</em> are the attributes a client has explicitly referenced in its
                queries. The list is not exhaustive &mdash; a client may also read the whole library through
                other requests, for example the classic API or an unrestricted <code>SELECT *</code>.
            </p>
            <b-table
                :data="groupedClientDependencies"
                :narrowed="true"
                :striped="true"
                :hoverable="true">
                <b-table-column
                    v-slot="tableProps"
                    field="client"
                    label="Client application"
                    sortable>
                    <router-link :to="getClientApplicationDetailsLink(tableProps.row.userId)">
                        {{ tableProps.row.client }}
                    </router-link>
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    label="Dependency">
                    <span
                        v-for="accessMechanism in tableProps.row.accessMechanisms"
                        :key="accessMechanism"
                        class="tag is-info mr-1 mb-1">{{ accessMechanism }}</span>
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    label="Observed attributes">
                    <span
                        v-if="tableProps.row.attributes.length === 0"
                        class="has-text-grey is-italic">None observed</span>
                    <span
                        v-for="attribute in tableProps.row.attributes"
                        :key="attribute"
                        class="tag is-light mr-1 mb-1">{{ attribute }}</span>
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="lastConfirmedDate"
                    label="Confirmed"
                    sortable>
                    {{ shortDate(tableProps.row.lastConfirmedDate) }}
                </b-table-column>
            </b-table>
        </div>
    </div>
</template>
